import { type UUID } from '~/types/common';

import { type DataSyncLogQueryParams } from './validations/types';

export const queryKeysDataSyncJob = {
  /**
   * Base key for all data sync job queries.
   * @returns The base query key.
   */
  base: () => ['dataSyncJobs'] as const,

  /**
   * Key for getting all data sync jobs.
   * @returns Query key array.
   */
  getAll: () => [...queryKeysDataSyncJob.base(), 'list'] as const,

  /**
   * Key for getting all data sync logs with optional query parameters.
   * @param params - Optional query parameters for filtering/pagination.
   * @returns Query key array.
   */
  getAllLogs: (params?: DataSyncLogQueryParams) =>
    [...queryKeysDataSyncJob.base(), 'logs', 'list', params ?? {}] as const,

  /**
   * Key for getting all data sync tasks.
   * @returns Query key array.
   */
  getAllTasks: () => [...queryKeysDataSyncJob.base(), 'tasks', 'list'] as const,

  /**
   * Key for getting a specific data sync job by ID.
   * @param jobId - The UUID of the data sync job.
   * @returns Query key array.
   */
  getById: (jobId: UUID) =>
    [...queryKeysDataSyncJob.base(), 'detail', { jobId }] as const,

  /**
   * Key for getting a specific data sync log by ID.
   * @param logId - The UUID of the data sync log.
   * @returns Query key array.
   */
  getLogById: (logId: UUID) =>
    [...queryKeysDataSyncJob.base(), 'log', 'detail', { logId }] as const,

  /**
   * Key for getting a specific data sync task by ID.
   * @param taskId - The UUID of the data sync task.
   * @returns Query key array.
   */
  getTaskById: (taskId: UUID) =>
    [...queryKeysDataSyncJob.base(), 'task', 'detail', { taskId }] as const,

  /**
   * Key for getting the per-system job templates.
   *
   * Deliberately not under `base()`: the templates ship with the backend and
   * change only when it deploys, so creating a job must not mark them stale
   * and refetch them along with the job list.
   *
   * @returns Query key array.
   */
  getTemplates: () => ['dataSyncTemplates', 'list'] as const,
} as const;
